import React, { useEffect, useMemo, useState } from 'react';
import {
  useStudentsActions,
  useStudentsState,
} from '../../contexts/students.context';
import { Grid } from '@mui/material';
import PaginatedGrid from '../../../../components/paginatedGrid/PaginatedGrid';
import {
  getColumnsForStudentGrid,
  getStudentGridRowsToRender,
} from './studentList.helpers';
import { AddButton } from '../../../../components/simple/AllButtons';
import StudentManageDialog from './StudentManageDialog.view';
import { TStudentPrimary } from '../../../../../apis/students/students.interfaces';
import { useStudentDetailsByIdHook } from '../../hooks/useStudentDetailsByIdHook';
import { useTranslation } from '../../../../../hooks/useTranslationHook';
import { PermissionGate } from '../../../../../components/rbac/PermissionGate';
import { useNavigate } from 'react-router-dom';
import { pages } from '../../../../../utils/common';

const StudentList: React.FC = () => {
  const { translateText } = useTranslation();
  const navigate = useNavigate();

  const studentListColumns = useMemo(
    () => getColumnsForStudentGrid(translateText),
    [translateText]
  );
  const {
    filteredStudents,
    isLoading,
    paginationParams,
    totalCount,
    selectedStudentId,
  } = useStudentsState();
  const {
    updatePaginationParams,
    setSelectedStudentId,
    setSelectedStudent,
    refreshStudentList,
  } = useStudentsActions();
  const [isOpenAddStudentDialog, setIsOpenAddStudentDialog] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const {
    data: selectedStudentDetails,
    isLoading: isSelectedStudentDetailsLoading,
  } = useStudentDetailsByIdHook(selectedStudentId ?? '', {
    enabled: !!selectedStudentId,
  });

  const onRowClickHandler = (params: any) => {
    const data = params.row as TStudentPrimary;
    if (data?.id) {
      navigate(`${pages.students.path}/${data.id}/details/profile`);
    }
  };

  const onAddStudentClickHandler = () => {
    setSelectedStudentId(null);
    setSelectedStudent(null);
    setIsOpenAddStudentDialog(true);
    setIsAddMode(true);
  };

  const onCloseAddStudentDialogHandler = (isRefreshStudentList: boolean = false) => {
    setIsOpenAddStudentDialog(false);
    setSelectedStudentId(null);
    setSelectedStudent(null);
    if (isRefreshStudentList) {
      refreshStudentList();
    }
  };

  const handlePageChange = (page: number, pageSize: number) => {
    updatePaginationParams({ page, limit: pageSize });
  };

  // const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
  //   updatePaginationParams({ page: newPage, limit: paginationParams.limit });
  // };

  // const handlePageSizeChange = (event: any) => {
  //   const newLimit = event.target.value;
  //   updatePaginationParams({ page: 1, limit: newLimit });
  // };

  useEffect(() => {
    if (selectedStudentId) {
      setSelectedStudent(selectedStudentDetails ?? null);
    } else {
      setSelectedStudent(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudentDetails]);

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2 }}
      sx={{
        p: { xs: 1, sm: 2, height: '100%' },
      }}
      data-testid="student-list-container"
    >
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'stretch', sm: 'flex-end' },
          mb: { xs: 1, sm: 0, height: '48px' },
        }}
        data-testid="student-list-header-container"
      >
        <PermissionGate
          module="students"
          submodule="studentList"
          permission="write"
        >
          <AddButton
            label={translateText('students.addStudent')}
            onClickHandler={onAddStudentClickHandler}
            data-testid="student-list-add-button"
            sx={{
              width: { xs: '100%', sm: 'auto' },
            }}
          />
        </PermissionGate>
      </Grid>
      <Grid item xs={12} sx={{ height: 'calc(100% - 48px)' }}>
        <PaginatedGrid
          rows={getStudentGridRowsToRender(filteredStudents) ?? []}
          columns={studentListColumns}
          loading={isLoading}
          dataTestId="student-list-grid"
          serverSide={true}
          totalCount={totalCount}
          currentPage={paginationParams.page}
          currentPageSize={paginationParams.limit}
          onPageChange={handlePageChange}
          rowsPerPageOptions={[1, 5, 10, 25, 50]}
          onRowClick={onRowClickHandler}
        />
      </Grid>
      <StudentManageDialog
        isOpen={
          isOpenAddStudentDialog &&
          (isAddMode || (!!selectedStudentId && !!selectedStudentDetails))
        }
        onClose={() => {
          onCloseAddStudentDialogHandler();
        }}
        errorMessage={''}
        studentDetails={
          !selectedStudentId ? null : (selectedStudentDetails ?? null)
        }
        isLoading={isSelectedStudentDetailsLoading}
      />
    </Grid>
  );
};

export default StudentList;
